import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow 
} from "@/components/UI/table";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/UI/card";
import { Button } from "@/components/UI/button";
import { Input } from "@/components/UI/input";
import { 
  Download, 
  RefreshCw, 
  Calendar, 
  ChevronLeft,
  FileSpreadsheet,
  Filter,
  Plus,
  Edit,
  Trash2,
  Search,
  BarChart
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/UI/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/UI/select";
import { useToast } from "@/components/UI/use-toast";
import { misApi } from '@/services/misApi';

const PLtrend = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(searchParams.get('year') || '2025');
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for cell editing
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // State for detail view
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Refs for sticky scrolling
  const tableContainerRef = useRef(null);
  
  // Define months in order
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun", 
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  
  // Fetch available years on component mount
  useEffect(() => {
    const fetchAvailableYears = async () => {
      try {
        const response = await misApi.getAvailableMonths();
        // Extract unique years from month strings (format: "Jan 2025")
        const monthsData = response.data.data || [];
        const years = [...new Set(monthsData.map(month => month.split(' ')[1]))];
        
        setAvailableYears(years);
        
        // Set default year if not in URL params
        if (!searchParams.get('year') && years.length > 0) {
          setSelectedYear(years[0]);
        }
      } catch (error) {
        console.error("Error fetching available years:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Could not fetch available years",
          duration: 3000,
        });
      }
    };
    
    fetchAvailableYears();
  }, [searchParams, toast]);
  
  // Fetch P&L trend data whenever the year changes
  useEffect(() => {
    if (selectedYear) {
      fetchPLTrendData();
    }
  }, [selectedYear]);
  
  // Apply sticky header and first column CSS
  useEffect(() => {
    if (!tableContainerRef.current) return;

    // Apply custom styles using CSS directly
    const style = document.createElement('style');
    style.textContent = `
      .sticky-header-cell {
        position: sticky;
        top: 0;
        z-index: 10;
        background-color: #f9fafb; /* gray-50 */
      }
      
      .sticky-first-col {
        position: sticky;
        left: 0;
        z-index: 9;
      }
      
      .sticky-first-col.sticky-header-cell {
        z-index: 11;
      }
      
      .sticky-header-row {
        position: sticky;
        top: 0;
        z-index: 10;
      }
      
      /* Set the background color based on cell type */
      .bg-header {
        background-color: #f9fafb; /* gray-50 */
      }
      
      .bg-white {
        background-color: white;
      }
      
      .bg-black {
        background-color: black;
        color: white;
      }
      
      .bg-yellow {
        background-color: #fef9c3; /* yellow-100 */
      }
      
      .gradient-header {
        background: linear-gradient(to right, #3b82f6, #10b981);
        color: white;
      }
      
      /* Remove all horizontal borders from table */
      table {
        border-collapse: collapse;
      }
      
      table tr {
        border-top: none;
        border-bottom: none;
      }
      
      table th, table td {
        border-top: none;
        border-bottom: none;
      }
      
      /* Only keep right borders for column separation */
      .border-r-only {
        border-right: 1px solid #e5e7eb;
        border-top: none;
        border-bottom: none;
        border-left: none;
      }
      
      /* Styling for top header border only */
      .header-border-only {
        border-top: 1px solid #e5e7eb;
        border-bottom: none;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  const fetchPLTrendData = async () => {
    setLoading(true);
    try {
      // Example API call - replace with your actual API endpoint for trend data
      const response = await misApi.getPLTrendReport(selectedYear);
      console.log("API Response for P&L Trend Data:", response.data.data);
      
      // Handle different response formats
      if (response.data.data) {
        setReportData(response.data.data);
      } else {
        // Fallback to empty data structure
        setReportData({
          categories: [],
          monthlyData: {}
        });
      }
      
      // For development/testing - mock data structure
      if (!response.data.data) {
        const mockData = {
          categories: [
            { id: 1, name: "REVENUE", isHeader: true, isTotal: false },
            { id: 2, name: "Direct Payroll", isHeader: false, isTotal: false },
            { id: 3, name: "Direct Expenses", isHeader: false, isTotal: false },
            { id: 4, name: "Materials", isHeader: false, isTotal: false },
            { id: 5, name: "TOTAL DIRECT COST", isHeader: false, isTotal: true },
            { id: 6, name: "TOTAL GROSS PROFIT", isHeader: false, isTotal: true },
            { id: 7, name: "TOTAL NET PROFIT FOR THE YEAR", isHeader: false, isTotal: true }
          ],
          monthlyData: {
            "Jan": {
              1: 12576166, // REVENUE
              2: -2021195, // Direct Payroll
              3: -10180851, // Direct Expenses
              4: -6114085, // Materials
              5: -12402048, // TOTAL DIRECT COST
              6: 174118, // TOTAL GROSS PROFIT
              7: -1277407 // TOTAL NET PROFIT
            },
            "Feb": {
              1: 7241288, // REVENUE
              2: -2220837, // Direct Payroll
              3: -9703370, // Direct Expenses
              4: -4200498, // Materials
              5: -12934207, // TOTAL DIRECT COST
              6: -5692919, // TOTAL GROSS PROFIT
              7: -5901792 // TOTAL NET PROFIT
            }
          },
          ytdData: {
            "actual": {
              1: 19817454, // REVENUE
              2: -4242032, // Direct Payroll
              3: -20084222, // Direct Expenses
              4: -10314583, // Materials
              5: -24336254, // TOTAL DIRECT COST
              6: -4508800, // TOTAL GROSS PROFIT
              7: -7179199 // TOTAL NET PROFIT
            },
            "budget": {
              1: 22009070, // REVENUE
              2: -5414008, // Direct Payroll
              3: -15483387, // Direct Expenses
              4: -6683852, // Materials
              5: -16897392, // TOTAL DIRECT COST
              6: 5111678, // TOTAL GROSS PROFIT
              7: 1417503 // TOTAL NET PROFIT
            }
          }
        };
        setReportData(mockData);
      }
    } catch (error) {
      console.error("Error fetching P&L trend data:", error);
      toast({
        variant: "destructive",
        title: "Failed to load data",
        description: error.response?.data?.message || "Could not load P&L trend data",
        duration: 3000,
      });
      // Set empty data on error
      setReportData({
        categories: [],
        monthlyData: {},
        ytdData: {
          actual: {},
          budget: {}
        }
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle year change
  const handleYearChange = (year) => {
    setSelectedYear(year);
    // Update URL params without navigation
    const params = new URLSearchParams(searchParams);
    params.set('year', year);
    navigate(`/MIS/pl-trend?${params.toString()}`, { replace: true });
  };
  
  // Handle export to Excel
  const handleExportToExcel = async () => {
    try {
      const response = await misApi.exportPLTrend(selectedYear);
      
      // Create a download link for the Excel file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pl_trend_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "Export Successful",
        description: "Trend report has been exported to Excel",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: "Could not export trend report to Excel",
        duration: 3000,
      });
    }
  };
  
  // Navigation functions
  const handleBackToDashboard = () => {
    navigate('/MIS');
  };
  
  // Cell editing functions
  const handleCellDoubleClick = (categoryId, month, value) => {
    const category = reportData.categories.find(cat => cat.id === categoryId);
    
    // Only allow editing for non-header and non-total rows
    if (!category || category.isHeader || category.isTotal) {
      return;
    }
    
    setEditingCell({
      categoryId,
      month
    });
    
    // Set current value for editing (remove parentheses if present)
    let editVal = value === null || value === undefined ? '' : value.toString();
    if (typeof value === 'number') {
      editVal = Math.abs(value).toString();
    } else if (editVal.startsWith('(') && editVal.endsWith(')')) {
      editVal = editVal.substring(1, editVal.length - 1).replace(/,/g, '');
    }
    setEditValue(editVal);
  };
  
  const handleCellEdit = async () => {
    if (!editingCell) return;
    
    try {
      const { categoryId, month } = editingCell;
      const numericValue = parseFloat(editValue);
      
      if (isNaN(numericValue)) {
        toast({
          variant: "destructive",
          title: "Invalid Value",
          description: "Please enter a valid number",
          duration: 3000,
        });
        return;
      }
      
      // Prepare the update data
      const updateData = {
        category_id: categoryId,
        month: `${month} ${selectedYear}`,
        value: numericValue
      };
      
      // Call the API to update the data
      await misApi.updatePLTrendData(updateData);
      
      toast({
        title: "Value Updated",
        description: "Trend data has been updated successfully",
        duration: 3000,
      });
      
      // Reset editing state
      setEditingCell(null);
      setEditValue('');
      
      // Refresh data
      await fetchPLTrendData();
      
    } catch (error) {
      console.error("Error updating cell value:", error);
      toast({
        variant: "destructive",
        title: "Failed to Update Value",
        description: error.response?.data?.message || "Could not update value",
        duration: 3000,
      });
    }
  };
  
  // Formatting functions
  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    
    if (typeof value === 'number') {
      // For percentage values
      if (Math.abs(value) < 1 && value !== 0) {
        return `${(value * 100).toFixed(1)}%`;
      }
      
      // Regular number formatting with commas
      // Only use parentheses for negative values
      if (value < 0) {
        return `(${Math.abs(value).toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })})`;
      }
      
      // For zero values, just show 0 
      if (value === 0) {
        return "0";
      }
      
      // For positive values, don't use parentheses
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
    
    return value;
  };
  
  // Filter categories based on search query
  const filteredCategories = reportData?.categories?.filter(category => 
    !searchQuery || category.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];
  
  // Loading skeleton
  const renderTableSkeleton = () => (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-4">
        <div className="h-8 w-64 bg-gray-200 animate-pulse rounded"></div>
        <div className="h-10 w-32 bg-gray-200 animate-pulse rounded"></div>
      </div>
      
      <div className="border rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4">
          <div className="h-6 col-span-1 bg-gray-200 animate-pulse rounded"></div>
          <div className="h-6 col-span-11 bg-gray-200 animate-pulse rounded"></div>
        </div>
        
        {[...Array(10)].map((_, i) => (
          <div key={i} className="grid grid-cols-12 gap-4 p-4">
            <div className="h-6 col-span-1 bg-gray-200 animate-pulse rounded"></div>
            {[...Array(11)].map((_, j) => (
              <div key={j} className="h-6 col-span-1 bg-gray-200 animate-pulse rounded"></div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
  
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackToDashboard}
            className="text-gray-600"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Button>
          <h1 className="text-xl font-semibold text-gray-800">Profit & Loss Trend</h1>
        </div>
        
        {renderTableSkeleton()}
      </div>
    );
  }
  
  // If no data is available
  if (!reportData || !reportData.categories || reportData.categories.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center space-x-4 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackToDashboard}
            className="text-gray-600"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Button>
          <h1 className="text-xl font-semibold text-gray-800">Profit & Loss Trend</h1>
        </div>
        
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-12">
            <FileSpreadsheet className="h-16 w-16 text-gray-300 mb-4" />
            <h2 className="text-xl font-medium text-gray-700 mb-2">No Data Available</h2>
            <p className="text-gray-500 text-center max-w-md">
              There's no profit and loss trend data available for the selected year.
              Please upload transaction data for at least one month to view the trend.
            </p>
            <Button
              className="mt-6 bg-[#C5A572] hover:bg-[#a68445]"
              onClick={handleBackToDashboard}
            >
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      {/* Header section with back button and controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackToDashboard}
            className="text-gray-600"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Button>
          <h1 className="text-xl font-semibold text-gray-800">Profit & Loss Trend</h1>
        </div>
        
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          {/* Year Selection */}
          <Select value={selectedYear} onValueChange={handleYearChange}>
            <SelectTrigger className="w-[180px]">
              <Calendar className="h-4 w-4 mr-2 text-gray-500" />
              <SelectValue placeholder="Select Year" />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map((year) => (
                <SelectItem key={year} value={year}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchPLTrendData}
            className="text-gray-600"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          
          {/* Export Button */}
          <Button 
            size="sm"
            onClick={handleExportToExcel}
            className="bg-[#C5A572] hover:bg-[#a68445]"
          >
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </div>
      </div>
      
      {/* Main content */}
      <Card className="shadow-sm">
        <CardHeader className="bg-gradient-to-r from-blue-500 to-green-500 text-white py-2 px-4">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-semibold flex items-center">
              <BarChart className="h-5 w-5 mr-2" />
              Profit & Loss Trend
            </CardTitle>
            <span className="text-sm font-medium">{`Year ${selectedYear}`}</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Search */}
          <div className="p-2 bg-gray-50 flex items-center">
            <div className="flex items-center space-x-2 w-64">
              <Search className="h-4 w-4 text-gray-500" />
              <Input 
                placeholder="Search categories..."
                className="h-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          {/* Table with fixed header and first column */}
          <div 
            className="overflow-auto max-h-[70vh]" 
            ref={tableContainerRef}
            style={{ position: 'relative' }}
          >
            <table className="w-full" style={{borderCollapse: 'collapse', borderSpacing: 0}}>
              <thead>
                <tr>
                  <th className="sticky-header-cell sticky-first-col bg-header p-2 border-r-only text-left min-w-[200px]" style={{backgroundColor: '#c6e0b4'}}>
                    <div className="flex items-center">
                      <span>Particulars</span>
                    </div>
                  </th>
                  
                  {/* Month Headers */}
                  {months.map((month) => (
                    <th 
                      key={month}
                      className="sticky-header-cell p-2 text-center border-r-only" 
                      style={{
                        backgroundColor: reportData.monthlyData[month] ? '#c6e0b4' : '#e5e7eb'
                      }}
                    >
                      {`${month}-${selectedYear.substring(2)}`}
                    </th>
                  ))}
                  
                  {/* YTD Columns */}
                  <th 
                    className="sticky-header-cell p-2 text-center border-r-only" 
                    style={{backgroundColor: '#d9d9d9'}}
                  >
                    YTD Actual
                  </th>
                  <th 
                    className="sticky-header-cell p-2 text-center" 
                    style={{backgroundColor: '#d9d9d9'}}
                  >
                    YTD Budget
                  </th>
                </tr>
              </thead>
              
              <tbody>
                {filteredCategories.map((category, index) => {
                  // Determine styling based on row type
                  const isBlackRow = 
                    category.name === "REVENUE" || 
                    category.name === "TOTAL DIRECT COST" || 
                    category.name === "TOTAL GROSS PROFIT" ||
                    category.name === "TOTAL GENERAL & ADMINISTRATIVE EXPENSES" || 
                    category.name === "TOTAL OPERATING PROFIT" || 
                    category.name === "TOTAL NET PROFIT FOR THE YEAR" ||
                    category.name === "Total Headcount";
                    
                  const isYellowRow = 
                    category.name === "GOP%" || 
                    category.name === "NOP%";
                  
                  // For regular categories, use blue text
                  const isRegularCategory = !category.isHeader && !category.isTotal && 
                                          !isBlackRow && !isYellowRow;
                  
                  const cellClass = isRegularCategory ? 'text-blue-600 italic font-medium' : '';
                  
                  return (
                    <tr 
                      key={index}
                      className={`h-5 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}
                      style={{borderTop: 'none', borderBottom: 'none'}}
                    >
                      <td className={`sticky-first-col p-2 border-r-only ${
                        isBlackRow ? 'bg-black text-white' : 
                        isYellowRow ? 'bg-yellow-100' : 'bg-white'
                      } ${cellClass}`} style={{borderTop: 'none', borderBottom: 'none'}}>
                        <span>{category.name}</span>
                      </td>
                      
                      {/* Month Value Cells */}
                      {months.map((month) => {
                        const value = reportData.monthlyData[month] ? 
                                    reportData.monthlyData[month][category.id] : 
                                    null;
                        
                        return (
                          <td 
                            key={`${category.id}-${month}`}
                            className={`p-2 text-right border-r-only ${
                              isBlackRow ? 'bg-black text-white' : 
                              isYellowRow ? 'bg-yellow-100' : ''
                            } ${cellClass}`}
                            style={{borderTop: 'none', borderBottom: 'none'}}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              handleCellDoubleClick(category.id, month, value);
                            }}
                          >
                            {editingCell && 
                             editingCell.categoryId === category.id && 
                             editingCell.month === month ? (
                              <input
                                type="text"
                                className="w-full h-6 p-1 text-right border rounded text-black bg-white"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={handleCellEdit}
                                onKeyDown={(e) => e.key === 'Enter' && handleCellEdit()}
                                autoFocus
                              />
                            ) : (
                              <span>{formatValue(value)}</span>
                            )}
                          </td>
                        );
                      })}
                      
                      {/* YTD Actual */}
                      <td 
                        className={`p-2 text-right border-r-only ${
                          isBlackRow ? 'bg-black text-white' : 
                          isYellowRow ? 'bg-yellow-100' : ''
                        } ${cellClass}`}
                        style={{borderTop: 'none', borderBottom: 'none'}}
                      >
                        <span>{formatValue(reportData.ytdData?.actual?.[category.id])}</span>
                      </td>
                      
                      {/* YTD Budget */}
                      <td 
                        className={`p-2 text-right ${
                          isBlackRow ? 'bg-black text-white' : 
                          isYellowRow ? 'bg-yellow-100' : ''
                        } ${cellClass}`}
                        style={{borderTop: 'none', borderBottom: 'none'}}
                      >
                        <span>{formatValue(reportData.ytdData?.budget?.[category.id])}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Footer with info about the data */}
      <div className="text-xs text-gray-500 mt-4">
        <p className="flex items-center">
          <Calendar className="h-3 w-3 mr-1" /> 
          Report generated for year: <span className="font-medium ml-1">{selectedYear}</span>
        </p>
        <p className="mt-1">
          Double-click on any cell to edit the value. Empty cells indicate months without data.
        </p>
      </div>
    </div>
  );
};

export default PLtrend;